import { HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AppGlobalActions } from '@app/_store/actions/global.actions';
import { AppActionsActions } from '@app/_store/features/app-actions/app-actions.actions';
import { selectActionToShow } from '@app/_store/features/app-actions/app-actions.selectors';
import { QuizFunnelActions } from '@app/_store/features/quiz-funnel/quiz-funnel.actions';
import { selectUserPlan } from '@app/_store/features/user-plan/user-plan.selectors';
import { selectQueryParams } from '@app/_store/selectors/router.selectors';
import { CheckoutDataProviderService } from '@core/checkout/checkout-data-provider.service';
import { StripeCheckoutParams } from '@core/checkout/models/stripe-checkout-params';
import { StripeCheckoutResponse } from '@core/checkout/models/stripe-checkout-response';
import { SuccessPurchaseModalComponent } from '@core/checkout/success-purchase-modal/success-purchase-modal.component';
import { AppDataConfig } from '@core/configs/app-data.config';
import { CONSTANTS } from '@core/constants/constants';
import { STATIC_LINKS } from '@core/constants/static-links';
import { SKIP_ERROR_TOAST } from '@core/interceptors/error.interceptor';
import { isApiError } from '@core/models/errors/api-error';
import { PopupInteractionRequest } from '@core/models/interfaces/http/popup-interaction-request';
import { StorageService } from '@core/services/storages/storage.service';
import { MetaPixelService } from '@core/services/tracking/meta-pixel.service';
import { MixpanelService } from '@core/services/tracking/mixpanel.service';
import { getDateComponentsFromString } from '@core/utils/utils';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  debounceTime,
  finalize,
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  switchMap,
  take,
  timeout,
} from 'rxjs';

// A19. `PURCHASE_PREMIUM_TEXTS`: this service is its only consumer.
const PURCHASE_PREMIUM_TEXTS = {
  FAILED_CHECKOUT_PAYMENT: (contactUrl: string) => {
    return (
      'Something went wrong. Please try again or&nbsp;contact&nbsp;us at&nbsp;' +
      contactUrl +
      ''
    );
  },
} as const;

/** How long the quiz funnel waits for its state update before giving up on it. */
const QUIZ_STATE_SETTLE_TIMEOUT_MS = 10_000;

/**
 * Where the user lands when nothing better is known.
 *
 * R128. Not `'/'`. Both root route blocks are `path: ''`, and the auth one comes
 * first — so `'/'` matches it, resolves its `'' -> sign-in` redirect, downloads
 * the lazy sign-in chunk, and only then runs `IsLoggedGuard`, which hands back a
 * `UrlTree` to `/dashboard`. Measured on the live app: three sign-in components
 * fetched and a second navigation, every time, for a user who is signed in. The
 * destination is identical; this one just goes there directly.
 */
const FALLBACK_URL = '/dashboard';

/** Which paywall a checkout came from, derived from the params it stored. */
interface CheckoutOrigin {
  isSpecialOffer: boolean;
  isQuizFunnel: boolean;
  isClickFunnel: boolean;
  isCancellation: boolean;
  isDiscount: boolean;
}

@Injectable({ providedIn: 'root' })
export class StripeRedirectProcessingService {
  private actions$ = inject(Actions);
  private router = inject(Router);
  private store = inject(Store);
  private storage = inject(StorageService);
  private gtm = inject(GoogleTagManagerService);
  private mixpanel = inject(MixpanelService);
  private metaPixel = inject(MetaPixelService);
  private toastr = inject(ToastrService);
  private matDialog = inject(MatDialog);
  private dataProvider = inject(CheckoutDataProviderService);

  private readonly storageKey = CONSTANTS.CHECKOUT_PARAMS_KEY;

  getCheckoutParams(): Observable<StripeCheckoutParams | null> {
    return this.storage.getField<StripeCheckoutParams>(this.storageKey);
  }

  /**
   * Runs the whole return-from-Stripe flow and answers the route guard.
   *
   * Every path through here has to end in a navigation. `/stripeCheckout` has an
   * empty component, so a path that neither navigates nor completes leaves the
   * user staring at a blank page — which is exactly what a declined card used to
   * do (R48).
   */
  onRedirect() {
    return this.store.select(selectQueryParams).pipe(
      take(1),
      switchMap((params) =>
        // A declined card comes back as the error envelope, and the failure
        // route below already tells the buyer what happened, with the support
        // link. The interceptor's generic toast on top of it made two toasts
        // for one failure — this request keeps only its own.
        this.dataProvider.getStripeData('stripeCheckout', params, {
          context: new HttpContext().set(SKIP_ERROR_TOAST, true),
        })
      ),
      switchMap((response) =>
        this.appDataReloaded$().pipe(map(() => response))
      ),
      switchMap((response) => this.redirectProcessing(response)),
      map(() => false),
      catchError((error: unknown) => {
        // The backend's error envelope — a declined card, an expired session —
        // is a *processed* outcome, not a broken redirect: it takes the failure
        // route back to where the checkout started, exactly as it did when the
        // envelope still arrived as a value.
        if (isApiError(error)) {
          return from(
            this.redirectProcessing(
              error.body as unknown as StripeCheckoutResponse
            )
          ).pipe(map(() => false));
        }
        throw error;
      }),
      catchError((error) => {
        console.error(
          'The checkout redirect could not be processed — sending the user back to the app:',
          error
        );
        this.router.navigateByUrl(FALLBACK_URL);
        return of(false);
      })
    );
  }

  /**
   * Reloads every container except transactions and resolves once that load has
   * settled, whichever way it went.
   *
   * R48. Two things used to be wrong here. The reload was dispatched only for a
   * successful payment, yet the wait for `loadAppDataSuccess` ran unconditionally
   * — so a declined card parked the guard on an action nobody would ever
   * dispatch, and the page never rendered. And even on the success path a failed
   * load would have hung it just the same, because failure was not an ending. It
   * is one now: the money has already moved, so the worst acceptable outcome is a
   * stale dashboard, never a dead page.
   */
  private appDataReloaded$(): Observable<unknown> {
    const settled$ = this.actions$.pipe(
      ofType(
        AppGlobalActions.loadAppDataSuccess,
        AppGlobalActions.loadAppDataFailure
      ),
      take(1)
    );

    // Transactions are left out on purpose: they page in on their own and a
    // reset would refetch every one of them for a subscription change.
    const fields = { ...AppDataConfig.containers };
    delete fields.transactions;

    setTimeout(() => {
      this.store.dispatch(
        AppGlobalActions.loadAppData({ fields, reset: true })
      );
    }, 500);

    return settled$;
  }

  private async redirectProcessing(
    response: StripeCheckoutResponse
  ): Promise<void> {
    const params = await firstValueFrom(this.getCheckoutParams());

    // R48. The params used to be read once in the constructor, into a signal
    // this method then read back. Whoever lost that race got `null` here and the
    // method returned without a word — after a *completed* payment that meant no
    // Meta Pixel, no GTM, no success popup and no navigation. Reading them where
    // they are needed removes the race; missing params are now a reported
    // degradation rather than a silent exit.
    if (!params) {
      console.error(
        'The stored checkout params are gone — finishing the redirect without purchase analytics or the success popup.'
      );
      await this.router.navigateByUrl(FALLBACK_URL);
      return;
    }

    const origin = this.resolveOrigin(params);

    if (response.status === 'success') {
      await this.processSuccess(response, params, origin);
    } else {
      await this.processFailure(params, origin);
    }
  }

  private resolveOrigin(params: StripeCheckoutParams): CheckoutOrigin {
    const entryPointUrl = params.entryPointUrl ?? '';

    return {
      isQuizFunnel: params.endpoint === 'quizPaywall',
      isCancellation: params.endpoint === 'cancellationPopup',
      isDiscount:
        params.endpoint === 'discountPaywall' ||
        params.endpoint === 'discountOffer',
      isClickFunnel: entryPointUrl.includes('/cf_checkout?'),
      isSpecialOffer: entryPointUrl.includes('/special-offer'),
    };
  }

  private async processSuccess(
    response: StripeCheckoutResponse,
    params: StripeCheckoutParams,
    origin: CheckoutOrigin
  ): Promise<void> {
    const eventData = {
      id: response.data.eventId,
      params: params.metaPixel?.eventParams,
      email: params.email || '',
    };

    await firstValueFrom(this.metaPixel.trackEvent('Subscribe', eventData));
    await firstValueFrom(
      this.metaPixel.trackEvent('subscribe_funnel', eventData)
    );

    if (params.metaPixel?.startTrial) {
      await firstValueFrom(this.metaPixel.trackEvent('StartTrial', eventData));
    }

    this.pushPurchaseToGtm(response, params);

    if (origin.isCancellation) {
      this.store.dispatch(AppActionsActions.clearAll());
    }

    if (origin.isQuizFunnel) {
      await this.completeQuizFunnel();
    }

    await this.completePurchase(params, origin);
  }

  /**
   * Reports the purchase to GTM.
   *
   * The push used to sit behind an empty `catch {}`, so a rejected tag was
   * indistinguishable from a delivered one and revenue quietly went missing from
   * reporting. `pushTag` can fail either way — synchronously or through its
   * promise — so both are caught and both say what was lost.
   */
  private pushPurchaseToGtm(
    response: StripeCheckoutResponse,
    params: StripeCheckoutParams
  ): void {
    const googleTagManager = params.googleTagManager;

    if (!googleTagManager?.events?.length) {
      console.warn(
        'The stored checkout params carry no Google Tag Manager events — this purchase will not reach GTM.'
      );
      return;
    }

    const eventParams = {
      ...googleTagManager.eventParams,
      transaction_id: response.data.eventId,
    };

    googleTagManager.events.forEach((event) => {
      const report = (err: unknown) =>
        console.error(
          `Google Tag Manager rejected the "${event}" event — this purchase will be missing from GTM reporting:`,
          err
        );

      try {
        this.gtm
          .pushTag({ event, eventModel: { ...eventParams } })
          .catch(report);
      } catch (err) {
        report(err);
      }
    });
  }

  /**
   * Marks the quiz as completed and waits for the reload that follows it.
   *
   * The wait is bounded. `updateQuizFunnelState$` has no `catchError`, so a
   * failed state update dispatches no `loadAppData` at all — and an unbounded
   * wait would strand a paying quiz-funnel user on the blank checkout page. The
   * quiz is the one paywall that is live in production, so this path gets to
   * finish even when the state update does not.
   */
  private async completeQuizFunnel(): Promise<void> {
    this.store.dispatch(
      QuizFunnelActions.updateQuizFunnelStateFromCheckout({
        updatedFromCheckout: true,
      })
    );
    this.store.dispatch(
      QuizFunnelActions.updateQuizFunnelState({
        next: { state: 'completed' },
      })
    );

    await firstValueFrom(
      this.actions$.pipe(
        ofType(
          AppGlobalActions.loadAppDataSuccess,
          AppGlobalActions.loadAppDataFailure
        ),
        debounceTime(500),
        timeout({
          first: QUIZ_STATE_SETTLE_TIMEOUT_MS,
          with: () => {
            console.warn(
              'The quiz funnel state did not settle in time — continuing to the app without waiting for it.'
            );
            return of(null);
          },
        })
      )
    );
  }

  private async processFailure(
    params: StripeCheckoutParams,
    origin: CheckoutOrigin
  ): Promise<void> {
    this.mixpanel.track(
      'Premium - Subscription error',
      params.trackingProps?.error
    );
    if (!origin.isSpecialOffer) this.updateAnalyticsFlags();

    this.toastr.warning(
      PURCHASE_PREMIUM_TEXTS.FAILED_CHECKOUT_PAYMENT(STATIC_LINKS.SUPPORT),
      'Error'
    );

    // TODO: restart the purchase here. The `restartInErrorCase` flag that used
    // to gate this was written `false` at its single producer and the branch was
    // empty, so the flag is gone — the intent is not.

    await this.navigateToEntryPoint(params);
  }

  private async completePurchase(
    params: StripeCheckoutParams,
    origin: CheckoutOrigin
  ): Promise<void> {
    this.mixpanel.track(
      'Premium - Start subscription',
      params.trackingProps?.success
    );
    this.mixpanel.setUserProperties({ isPremium: true });

    if (origin.isClickFunnel) {
      // R48. A click funnel only ever set its analytics flags here and then let
      // the guard reject the route, which left the buyer on the empty checkout
      // page. The flags still get set; the user now also gets somewhere to be.
      this.updateAnalyticsFlags();
      await this.router.navigateByUrl(FALLBACK_URL);
      return;
    }

    if (origin.isDiscount) {
      await this.sendDiscountPopupInteractionRequest();
    }

    this.updateAnalyticsFlags();

    // R119. `removeField` reports its failures now, and this one sits directly
    // in front of the navigation that gets the user off the checkout page after
    // a completed purchase. A storage hiccup must not strand them there.
    try {
      await firstValueFrom(this.storage.removeField(this.storageKey));
    } catch (err) {
      console.error(
        'Could not clear the stored checkout params after a completed checkout — the next one may start from stale ones:',
        err
      );
    }

    await this.router.navigateByUrl(this.successUrl(params, origin));

    if (params.messageInSuccessCase) {
      await this.showSuccessPopup(params);
    }
  }

  /**
   * POC-9481: a purchase made in the app ends where it started — every one of
   * them used to land on the dashboard. Legacy's `checkoutReturnPage` sent a
   * completed checkout back to its `entryPointUrl`, except the cancellation and
   * discount offers, which went to the dashboard; those stay. So do three that
   * went there before this change: the quiz (live in production, left as it
   * ships), the blocked paywall (its guard turns a paid user away from
   * `/blocked` through `/`) and the special offer (it has no page here).
   */
  private successUrl(
    params: StripeCheckoutParams,
    origin: CheckoutOrigin
  ): string {
    const leavesForDashboard =
      origin.isQuizFunnel ||
      origin.isCancellation ||
      origin.isDiscount ||
      origin.isSpecialOffer ||
      params.endpoint === 'blockedPaywall';

    return (!leavesForDashboard && params.entryPointUrl) || FALLBACK_URL;
  }

  /**
   * Sends the user back where the checkout started.
   *
   * R48. This was `navigate([url])`, which treats the whole string as a single
   * path segment and percent-encodes everything in it: `/cf_checkout?utm=abc`
   * came back as `/cf_checkout%3Futm%3Dabc`. `entryPointUrl` comes from
   * `selectUrl` and carries the query string — the click-funnel and special-offer
   * checks in `resolveOrigin` rely on that — so every entry point with
   * parameters landed on a route that does not exist.
   */
  private async navigateToEntryPoint(
    params: StripeCheckoutParams
  ): Promise<void> {
    await this.router.navigateByUrl(params.entryPointUrl || FALLBACK_URL);
  }

  private async showSuccessPopup(params: StripeCheckoutParams) {
    const userPlan = await firstValueFrom(this.store.select(selectUserPlan));
    const dialogData = {
      name: params.subscriptionName || null,
      period: getDateComponentsFromString(userPlan?.planDetails.renewDate)
        ?.timestamp,
    };

    this.matDialog.open(SuccessPurchaseModalComponent, {
      data: dialogData,
      enterAnimationDuration: 0,
      panelClass: 'default-modal',
      // POC-9483: the default focuses the first tabbable — this popup's only
      // button — and the popup opens on a page Stripe has just loaded, with no
      // click yet, so the browser draws that focus as a ring. The container
      // takes the focus instead; Tab still reaches the button.
      autoFocus: 'dialog',
    });
  }

  private async sendDiscountPopupInteractionRequest() {
    const discountAction = await firstValueFrom(
      this.store.select(selectActionToShow)
    );
    const payload: PopupInteractionRequest = {
      popupType: 'discountOffer',
      action: 'dismiss',
      params: {
        flashId: discountAction?.params?.flashId,
      },
    };

    // R129. No plan-state refresh here: `appDataReloaded$()` has already reloaded every
    // container and been waited on, two seconds earlier in this same flow. Measured before the
    // change — the dismiss was followed by a second `fetchData` for `userPlanState` alone.
    this.dataProvider
      .popupInteraction(payload, false)
      .pipe(
        take(1),
        // The interceptor has toasted it; the actions are cleared either way.
        catchError((error: unknown) => {
          console.warn(
            'Discount offer dismissal was not recorded after checkout — it may show again on the next sign-in',
            error
          );
          return of(null);
        }),
        finalize(() => this.store.dispatch(AppActionsActions.clearAll()))
      )
      .subscribe();
  }

  private updateAnalyticsFlags() {
    sessionStorage.setItem('OpenWebApp', '1');
    sessionStorage.setItem('appOpened', '1');
  }
}
